// Turns the positioned text of a 1099 PDF into candidates for the organizer's form editors.
//
// Rules this parser keeps:
// - It anchors on box numbers plus keywords ("realized", "unrealized", "aggregate"), because substitute
//   statements use their own wording and layout (Pub 1179 lets the filer choose).
// - A value is taken only when exactly one amount sits where the form puts it: to the right of the label on
//   the same line, or in the label's column just below. Anything else is "not found" or "ambiguous".
// - It never fills a zero, never changes a printed figure, and never reads a year from the form's revision date.
// - Losses in parentheses or with a minus sign stay negative. The printed text travels with the value.
import { amountFrom, type Amount } from "@ftr/tax-core";
import { buildLines, columnText, xAt, type Line, type TextItem } from "./text.ts";

export type Found<T> = { found: true; value: T; raw: string; page: number } | { found: false; reason: "not_found" | "ambiguous"; raw?: string; page?: number };

export const NOT_FOUND: Found<never> = { found: false, reason: "not_found" };

export interface Form1099BCandidate {
  kind: "form_1099b";
  taxYear: Found<number>;
  issuer: Found<string>;
  description: Found<string>;
  corrected: Found<boolean>;
  box4: Found<Amount>;
  box8: Found<Amount>;
  box9: Found<Amount>;
  box10: Found<Amount>;
  box11: Found<Amount>;
  pages: number[];
}

export interface PayerFormCandidate {
  kind: "form_1099nec" | "form_1099misc" | "form_1099k";
  taxYear: Found<number>;
  issuer: Found<string>;
  corrected: Found<boolean>;
  incomeBox: Found<Amount>;
  box4: Found<Amount>;
  pages: number[];
}

export type FormCandidate = Form1099BCandidate | PayerFormCandidate;

export interface ParsedDocument {
  forms: FormCandidate[];
  /** Form names seen in the text, e.g. "1099-INT", whether or not they produced a candidate. */
  formsSeen: string[];
  /** True when the PDF had any text at all. False usually means a scanned image. */
  hasText: boolean;
  notes: string[];
}

export const FORM_NAME: Record<FormCandidate["kind"], string> = {
  form_1099b: "1099-B",
  form_1099nec: "1099-NEC",
  form_1099misc: "1099-MISC",
  form_1099k: "1099-K",
};

// ---------------------------------------------------------------- labels

type Box = "b8" | "unrealized" | "b11" | "b4" | "b1a" | "ctype" | "nec1a" | "misc3" | "k1a" | "payer" | "calyear" | "corrected" | "form" | "label";

interface Anchor {
  box: Box;
  line: Line;
  lineIndex: number;
  page: number;
  /** x where the label starts (its box number when one is printed) and where its matched text ends. */
  x: number;
  xEnd: number;
  /** Year printed inside the label, e.g. "realized in 2026" or "12/31/2025". */
  year?: number;
  /** Box number printed right before the label text. */
  prefix?: number;
  /** For "ctype": the contract type as printed. For "form": the form name. */
  text?: string;
}

/** Anchors that are headings or flags, not boxes. They never bound a value search. */
const NON_BOX: ReadonlySet<Box> = new Set<Box>(["corrected", "form", "ctype", "calyear"]);

const INSTRUCTION_LINE = /^\s*box(es)?\s+\d+[a-z]?(\s*(,|and|through|-)\s*\d+[a-z]?)*\s*[.:]/i;
const INSTRUCTION_PAGE = /instructions\s+for\s+recipient/i;
const REV_LINE = /\brev\.?\s*\(?\s*(\d{1,2}[-/]\d{4}|[a-z]+\s+\d{4}|\d{4})/i;

const B8 = /profit\s*(?:or|\/|and)?\s*\(?loss\)?\s*realized\b|\brealized\s+profit\s*(?:or|\/|and)?\s*\(?loss\)?/i;
const UNREALIZED = /unrealized\s*profit\s*(?:or|\/|and)?\s*\(?loss\)?/i;
const B11 = /aggregate\s*profit/i;
const B4 = /federal\s*income\s*tax\s*withheld/i;
const B1A = /^\s*1a\b|description\s+of\s+property/i;
const CTYPE = /digital\s+asset\s+(?:regulated\s+futures|(?:section\s*)?1256\s+options?)(?:\s+contracts?)?|regulated\s+futures(?:\s+contracts?)?|foreign\s+currency\s+contracts?|(?:section\s*)?1256\s+options?(?:\s+contracts?)?/i;
const NEC1A = /nonemployee\s+compensation/i;
const MISC3 = /\bother\s+income\b/i;
const K1A = /gross\s+amount\s+of\s+payment/i;
const PAYER = /payer'?s?\s+name/i;
const CALYEAR = /calendar\s+year/i;
/** The second line of a wrapped IRS label, which is not a value. */
const LABEL_CONTINUATION = /^(or\s+foreign|and\s+telephone|street\s+address|city\s+or\s+town|state\s+or\s+province|country|zip\s+or|telephone\s+no|postal\s+code)/i;
const CORRECTED = /corrected/i;
const FORM = /\b1099[-\s]?(INT|DIV|OID|MISC|NEC|K|R|B|S|G|Q)\b/i;
const GENERIC = [/recipient'?s?\s+name/i, /payer'?s?\s+tin/i, /recipient'?s?\s+tin/i, /account\s+number/i, /omb\s+no/i, /applicable\s+check/i];
const NUMBERED_LABEL = /(?:^|\s)(\d{1,2})[a-g]?\s+[A-Z][a-z]/g;
const PREFIX_BEFORE = /(?:^|\s)(\d{1,2})[a-g]?\s*[.:)-]?\s*$/;
const DATE_YEAR = /12\s*\/\s*31\s*\/\s*(20\d{2}|\d{2})\b|december\s+31\s*,?\s*(20\d{2})/i;
const YEAR_AFTER = /(?<![/\d])\b(20\d{2})\b(?!\s*\/)/;

/** Where a label starts: its box number when one is printed, or the start of its text run when the match begins a word or two in ("For calendar year"). */
function labelStartX(line: Line, index: number): number {
  const span = line.spans.find((s) => index >= s.start && index < s.end);
  if (span && line.text.slice(span.start, index).trim().length <= 4) return span.item.x;
  return xAt(line, index);
}

function anchorAt(box: Box, line: Line, lineIndex: number, index: number, length: number, extra: Partial<Anchor> = {}): Anchor {
  const before = line.text.slice(0, index);
  const pm = PREFIX_BEFORE.exec(before);
  const startIndex = pm ? pm.index + (pm[0].length - pm[0].trimStart().length) : index;
  const xEnd = xAt(line, Math.max(index, index + length - 1)) + 1;
  return { box, line, lineIndex, page: line.page, x: pm ? xAt(line, startIndex) : labelStartX(line, index), xEnd, prefix: pm ? Number(pm[1]) : undefined, ...extra };
}

function instructionPages(lines: Line[]): Set<number> {
  const counts = new Map<number, number>();
  const pages = new Set<number>();
  for (const l of lines) {
    if (INSTRUCTION_PAGE.test(l.text)) pages.add(l.page);
    if (/^\s*box(es)?\s+\d/i.test(l.text)) counts.set(l.page, (counts.get(l.page) ?? 0) + 1);
  }
  for (const [page, n] of counts) if (n >= 3) pages.add(page);
  return pages;
}

function findAnchors(lines: Line[]): Anchor[] {
  const out: Anchor[] = [];
  const skipBoxes = instructionPages(lines);
  lines.forEach((line, i) => {
    const t = line.text;
    const add = (box: Box, m: RegExpExecArray, extra: Partial<Anchor> = {}) => out.push(anchorAt(box, line, i, m.index, m[0].length, extra));
    let m: RegExpExecArray | null;
    if ((m = FORM.exec(t))) add("form", m, { text: `1099-${m[1]!.toUpperCase()}` });
    if ((m = PAYER.exec(t))) add("payer", m);
    if ((m = CALYEAR.exec(t))) add("calyear", m);
    if ((m = CORRECTED.exec(t))) add("corrected", m);
    if (skipBoxes.has(line.page) || INSTRUCTION_LINE.test(t) || t.length > 140) return;
    if ((m = UNREALIZED.exec(t))) {
      const rest = t.slice(m.index + m[0].length, m.index + m[0].length + 60);
      const d = DATE_YEAR.exec(rest);
      const y = d ? Number(d[1] ?? d[2]) : undefined;
      add("unrealized", m, { year: y === undefined ? undefined : y < 100 ? 2000 + y : y });
    }
    if ((m = B8.exec(t))) {
      const after = t.slice(m.index + m[0].length, m.index + m[0].length + 80);
      const y = YEAR_AFTER.exec(after);
      add("b8", m, { year: y ? Number(y[1]) : undefined });
    }
    if ((m = B11.exec(t))) add("b11", m);
    if ((m = B4.exec(t))) add("b4", m);
    if ((m = B1A.exec(t))) add("b1a", m);
    if ((m = CTYPE.exec(t))) add("ctype", m, { text: m[0].replace(/\s+/g, " ").trim() });
    if ((m = NEC1A.exec(t))) add("nec1a", m);
    if ((m = MISC3.exec(t))) add("misc3", m);
    if ((m = K1A.exec(t))) add("k1a", m);
    const taken = (index: number) => out.some((a) => a.lineIndex === i && Math.abs(a.x - xAt(line, index)) < 3);
    for (const g of GENERIC) if ((m = g.exec(t)) && !taken(m.index)) add("label", m);
    NUMBERED_LABEL.lastIndex = 0;
    while ((m = NUMBERED_LABEL.exec(t))) {
      const index = m.index + (m[0].length - m[0].trimStart().length);
      if (!taken(index)) out.push(anchorAt("label", line, i, index, m[0].length - (index - m.index)));
    }
  });
  return out;
}

// ---------------------------------------------------------------- amounts

export interface MoneyToken {
  x: number;
  raw: string;
  amount: Amount;
}

// Leading "(" or "-", optional "$", digits with thousands separators and optional cents, closing ")" or trailing "-".
const MONEY = /(?<![\w./-])(\(|-)?\s?(\$)?\s?(\()?\s?(\d{1,3}(?:,\d{3})+|\d+)(\.\d{1,2})?\s?(\))?(-)?(?![\w./%-])/g;

/** Amounts printed on a line. A bare integer is not an amount: it could be a year, a box number or a count. */
export function moneyTokens(line: Line): MoneyToken[] {
  const out: MoneyToken[] = [];
  MONEY.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MONEY.exec(line.text))) {
    const [all, open1, dollar, open2, digits, cents, close, trailingMinus] = m;
    if (!cents && !digits!.includes(",") && !dollar) continue;
    const opens = open1 === "(" || Boolean(open2);
    if (opens !== Boolean(close)) continue;
    const negative = opens || open1 === "-" || Boolean(trailingMinus);
    const amount = amountFrom((negative ? "-" : "") + digits!.replace(/,/g, "") + (cents ?? ""));
    if (amount.state !== "value") continue;
    // The printed text, without a preprinted "$" in front of the box.
    const raw = all.trim().replace(/^\$\s*/, "");
    out.push({ x: xAt(line, m.index + (all.length - all.trimStart().length)), raw, amount: { state: "value", value: amount.value, raw } });
  }
  return out;
}

const MAX_DROP = 70;

function rightBoundOf(anchor: Anchor, anchors: Anchor[]): number {
  const sameLine = anchors.filter((a) => a !== anchor && a.lineIndex === anchor.lineIndex && a.x > anchor.x + 1 && !NON_BOX.has(a.box));
  return sameLine.length ? Math.min(...sameLine.map((a) => a.x)) : Infinity;
}

/** The amount that belongs to a label: to its right on the same line, or in its column just below. */
function valueFor(anchor: Anchor, lines: Line[], anchors: Anchor[]): Found<Amount> {
  const rightBound = rightBoundOf(anchor, anchors);
  const pick = (tokens: MoneyToken[], page: number): Found<Amount> | null => {
    if (tokens.length === 1) return { found: true, value: tokens[0]!.amount, raw: tokens[0]!.raw, page };
    if (tokens.length > 1) return { found: false, reason: "ambiguous", raw: tokens.map((t) => t.raw).join("  "), page };
    return null;
  };
  const here = moneyTokens(anchor.line).filter((t) => t.x >= anchor.xEnd - 2 && t.x < rightBound);
  const found = pick(here, anchor.page);
  if (found) return found;

  const left = anchor.x - 10;
  for (let i = anchor.lineIndex + 1; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.page !== anchor.page || anchor.line.y - line.y > MAX_DROP) break;
    if (anchors.some((a) => a.lineIndex === i && a.x >= left && a.x < rightBound && !NON_BOX.has(a.box))) break;
    const got = pick(moneyTokens(line).filter((t) => t.x >= left && t.x < rightBound), line.page);
    if (got) return got;
  }
  return NOT_FOUND;
}

/** Text in a label's column on the first line below it, for the payer name and box 1a. */
function textBelow(anchor: Anchor, lines: Line[], anchors: Anchor[], maxDrop = 45, requireLetters = true): Found<string> {
  const rightBound = rightBoundOf(anchor, anchors);
  for (let i = anchor.lineIndex + 1; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.page !== anchor.page || anchor.line.y - line.y > maxDrop) break;
    const text = columnText(line, anchor.x - 8, rightBound);
    if (!text || (requireLetters && !/[A-Za-z]/.test(text)) || LABEL_CONTINUATION.test(text)) continue;
    if (anchors.some((a) => a.lineIndex === i && a.x >= anchor.x - 8 && a.x < rightBound && a.box !== "corrected" && a.box !== "ctype")) return NOT_FOUND;
    return { found: true, value: text, raw: text, page: line.page };
  }
  return NOT_FOUND;
}

// ---------------------------------------------------------------- document-level facts

const MARKS = new Set(["x", "☒", "✓", "✔", "■", "●", "◼", "▪"]);

/** Whether the form is marked CORRECTED. A checkbox we can't read stays "not found" rather than guessed. */
function correctedOn(page: number | null, anchors: Anchor[]): Found<boolean> {
  const hits = anchors.filter((a) => a.box === "corrected" && (page === null || a.page === page));
  if (page !== null && hits.length === 0) return correctedOn(null, anchors);
  let unsure: Found<boolean> | null = null;
  for (const a of hits) {
    const t = a.line.text.trim();
    const checkbox = /corrected\s*\(?\s*if\s+checked\)?/i.exec(t) ?? /^(void\s+)?corrected$/i.exec(t);
    if (checkbox) {
      const mark = a.line.spans.find((s) => s.item.x < a.x && s.item.x > a.x - 30 && MARKS.has(s.item.str.trim().toLowerCase()));
      if (mark) return { found: true, value: true, raw: `${mark.item.str.trim()} ${checkbox[0]}`, page: a.page };
      unsure ??= { found: false, reason: "not_found", raw: checkbox[0], page: a.page };
      continue;
    }
    // A title such as "Corrected Form 1099-B" or "CORRECTED Consolidated 1099", not a sentence from the instructions.
    if (t.length <= 90 && !/[.;]/.test(t) && /1099|form|statement|consolidated/i.test(t)) return { found: true, value: true, raw: t, page: a.page };
  }
  if (unsure) return unsure;
  return { found: true, value: false, raw: "The PDF text has no CORRECTED mark or title", page: page ?? 1 };
}

const YEAR_PATTERNS = [/calendar\s+year\s*:?\s*(20)\s?(\d{2})\b/i, /tax\s+year\s*:?\s*(20)(\d{2})\b/i, /\b(20)(\d{2})\s+(?:form\s+)?1099/i, /1099[-\w]*\s+(?:for\s+)?(20)(\d{2})\b/i];

/** The tax year the form is for, from the filled-in year or the years in the box labels. Never from "Rev." */
function taxYearOf(lines: Line[], anchors: Anchor[]): Found<number> {
  const votes = new Map<number, { raw: string; page: number }>();
  for (const a of anchors) if (a.box === "b8" && a.year) votes.set(a.year, { raw: a.line.text.trim(), page: a.page });
  const unrealized = anchors.filter((a) => a.box === "unrealized" && a.year);
  if (unrealized.length >= 2) {
    const years = unrealized.map((a) => a.year!);
    const max = Math.max(...years);
    if (max - Math.min(...years) === 1) votes.set(max, { raw: `open contracts at 12/31/${max - 1} and 12/31/${max}`, page: unrealized[0]!.page });
  }
  for (const line of lines) {
    if (REV_LINE.test(line.text)) continue;
    for (const p of YEAR_PATTERNS) {
      const m = p.exec(line.text);
      if (m) votes.set(Number(m[1]! + m[2]!), { raw: line.text.trim(), page: line.page });
    }
  }
  // "For calendar year" with the year printed in the box below it, as on the continuous-use NEC, MISC and K forms.
  for (const a of anchors) {
    if (a.box !== "calyear" || /calendar\s+year\s*:?\s*20/i.test(a.line.text)) continue;
    const below = textBelow(a, lines, anchors, 30, false);
    if (!below.found) continue;
    const m = /^(20)\s?(\d{2})\b/.exec(below.value);
    if (m) votes.set(Number(m[1]! + m[2]!), { raw: `${a.line.text.trim()} ${below.value}`, page: a.page });
  }
  const years = [...votes.keys()];
  if (years.length === 1) return { found: true, value: years[0]!, ...votes.get(years[0]!)! };
  if (years.length > 1) return { found: false, reason: "ambiguous", raw: years.sort().join(", ") };
  return NOT_FOUND;
}

/** Legal payer names seen on broker and prop-firm forms. Used only to recognise a name, never to invent one. */
const KNOWN_PAYERS = [
  "NinjaTrader Clearing",
  "Phillip Capital",
  "Apex Clearing",
  "Interactive Brokers",
  "Robinhood",
  "TradeStation Securities",
  "Charles Schwab Futures",
  "ADM Investor Services",
  "Dorman Trading",
  "Ironbeam",
  "StoneX",
  "Wedbush",
  "AMP Global",
  "Plus500US",
  "Webull Futures",
  "E*TRADE Futures",
  "Apex Trader Funding",
  "Tradeify",
  "Novaflame",
  "Topstep",
];

function payerOn(page: number | null, lines: Line[], anchors: Anchor[]): Found<string> {
  const labels = anchors.filter((a) => a.box === "payer" && (page === null || a.page === page));
  for (const a of labels) {
    const below = textBelow(a, lines, anchors);
    if (below.found) return below;
  }
  if (page !== null) return payerOn(null, lines, anchors);
  for (const line of lines) {
    const t = line.text.trim();
    if (t.length > 80 || FORM.test(t)) continue;
    if (KNOWN_PAYERS.some((p) => t.toLowerCase().includes(p.toLowerCase()))) return { found: true, value: t, raw: t, page: line.page };
  }
  return NOT_FOUND;
}

// ---------------------------------------------------------------- 1099-B box sets

type SetKey = "b8" | "b9" | "b10" | "b11";

interface BoxSet {
  page: number;
  boxes: Partial<Record<SetKey, Anchor>>;
  unrealized: Anchor[];
  first: Anchor;
  last: Anchor;
}

/** Group box 8-11 labels into sets, one per contract type. A repeated box or a heading starts a new set. */
function buildSets(anchors: Anchor[]): BoxSet[] {
  const ordered = anchors.filter((a) => a.box === "b8" || a.box === "unrealized" || a.box === "b11" || a.box === "form" || a.box === "ctype");
  const sets: BoxSet[] = [];
  let cur: BoxSet | null = null;
  const flush = () => {
    if (cur) sets.push(cur);
    cur = null;
  };
  ordered.forEach((a, i) => {
    if (a.box === "form" || a.box === "ctype") {
      const sharesLine = ordered.some((n, j) => j !== i && n.lineIndex === a.lineIndex && n.box !== "form" && n.box !== "ctype");
      if (cur && !sharesLine && a.lineIndex !== cur.last.lineIndex) flush();
      return;
    }
    const full = a.box === "unrealized" ? cur && cur.unrealized.length >= 2 : cur && cur.boxes[a.box as "b8" | "b11"];
    if (cur && (full || cur.page !== a.page)) flush();
    if (!cur) cur = { page: a.page, boxes: {}, unrealized: [], first: a, last: a };
    if (a.box === "unrealized") cur.unrealized.push(a);
    else cur.boxes[a.box as "b8" | "b11"] = a;
    cur.last = a;
  });
  flush();
  return sets;
}

/** Decide which unrealized label is box 9 (prior year-end) and which is box 10 (this year-end). */
function assignUnrealized(set: BoxSet, taxYear: number | null): void {
  const u = set.unrealized;
  const byYear = (a: Anchor): "b9" | "b10" | null => {
    if (a.year && taxYear) return a.year === taxYear - 1 ? "b9" : a.year === taxYear ? "b10" : null;
    if (a.prefix === 9) return "b9";
    if (a.prefix === 10) return "b10";
    return null;
  };
  const keys = u.map(byYear);
  if (u.length === 2 && keys[0] === null && keys[1] === null && u[0]!.year && u[1]!.year && u[0]!.year !== u[1]!.year) {
    keys[0] = u[0]!.year < u[1]!.year ? "b9" : "b10";
    keys[1] = keys[0] === "b9" ? "b10" : "b9";
  }
  if (u.length === 2 && (keys[0] === null) !== (keys[1] === null)) {
    const known = keys[0] ?? keys[1]!;
    const other = known === "b9" ? "b10" : "b9";
    if (keys[0] === null) keys[0] = other;
    else keys[1] = other;
  }
  u.forEach((a, i) => {
    const k = keys[i];
    if (k && !set.boxes[k]) set.boxes[k] = a;
  });
}

function descriptionFor(set: BoxSet, lines: Line[], anchors: Anchor[], prevEnd: number): Found<string> {
  const candidates = anchors.filter((a) => (a.box === "b1a" || a.box === "ctype") && a.page === set.page && a.lineIndex < set.first.lineIndex && a.lineIndex > prevEnd && set.first.lineIndex - a.lineIndex <= 40);
  for (const a of candidates.sort((p, q) => q.lineIndex - p.lineIndex)) {
    if (a.box === "ctype") return { found: true, value: a.text!, raw: a.line.text.trim(), page: a.page };
    const at = a.line.text.search(B1A);
    const rest = a.line.text
      .slice(at)
      .replace(B1A, "")
      .replace(/\(example.*?\)/i, "")
      .trim();
    const inline = CTYPE.exec(rest);
    if (inline) return { found: true, value: inline[0].replace(/\s+/g, " ").trim(), raw: a.line.text.trim(), page: a.page };
    const below = textBelow(a, lines, anchors);
    if (below.found) return below;
  }
  return NOT_FOUND;
}

/** Line range of the form section a set sits in, bounded by the nearest form headings above and below. */
function sectionRange(set: BoxSet, anchors: Anchor[]): { top: number; bottom: number } {
  const forms = anchors.filter((a) => (a.box === "form" || a.box === "ctype") && a.page === set.page && a.lineIndex !== set.first.lineIndex && a.lineIndex !== set.last.lineIndex);
  const above = forms.filter((a) => a.lineIndex < set.first.lineIndex).map((a) => a.lineIndex);
  const below = forms.filter((a) => a.lineIndex > set.last.lineIndex).map((a) => a.lineIndex);
  return { top: above.length ? Math.max(...above) : -1, bottom: below.length ? Math.min(...below) : Infinity };
}

function box4For(set: BoxSet, lines: Line[], anchors: Anchor[]): Found<Amount> {
  const range = sectionRange(set, anchors);
  const hits = anchors.filter((a) => a.box === "b4" && a.page === set.page && a.lineIndex > range.top && a.lineIndex < range.bottom && Math.abs(a.line.y - set.first.line.y) <= 260);
  if (hits.length === 1) return valueFor(hits[0]!, lines, anchors);
  return hits.length ? { found: false, reason: "ambiguous", page: set.page } : NOT_FOUND;
}

function sameAmount(a: Found<Amount>, b: Found<Amount>): boolean {
  if (a.found !== b.found) return false;
  return a.found && b.found ? a.value.state === "value" && b.value.state === "value" && a.value.value === b.value.value : true;
}

function same1099B(a: Form1099BCandidate, b: Form1099BCandidate): boolean {
  const d = (f: Found<string>) => (f.found ? f.value.toLowerCase() : null);
  return d(a.description) === d(b.description) && (["box4", "box8", "box9", "box10", "box11"] as const).every((k) => sameAmount(a[k], b[k]));
}

// ---------------------------------------------------------------- entry point

export function parse1099(items: TextItem[]): ParsedDocument {
  const lines = buildLines(items);
  const hasText = lines.some((l) => l.text.trim().length > 0);
  const anchors = findAnchors(lines);
  const notes: string[] = [];
  const formsSeen = [...new Set(anchors.filter((a) => a.box === "form").map((a) => a.text!))];
  const docYear = taxYearOf(lines, anchors);
  const forms: FormCandidate[] = [];

  // Section 1256 box sets, one per contract type.
  const bForms: Form1099BCandidate[] = [];
  let prevEnd = -1;
  for (const set of buildSets(anchors)) {
    const b8Year = set.boxes.b8?.year;
    assignUnrealized(set, b8Year ?? (docYear.found ? docYear.value : null));
    const val = (a?: Anchor) => (a ? valueFor(a, lines, anchors) : NOT_FOUND);
    const cand: Form1099BCandidate = {
      kind: "form_1099b",
      taxYear: b8Year ? { found: true, value: b8Year, raw: set.boxes.b8!.line.text.trim(), page: set.page } : docYear,
      issuer: payerOn(set.page, lines, anchors),
      description: descriptionFor(set, lines, anchors, prevEnd),
      corrected: correctedOn(set.page, anchors),
      box4: box4For(set, lines, anchors),
      box8: val(set.boxes.b8),
      box9: val(set.boxes.b9),
      box10: val(set.boxes.b10),
      box11: val(set.boxes.b11),
      pages: [set.page],
    };
    prevEnd = set.last.lineIndex;
    if (!(["box8", "box9", "box10", "box11"] as const).some((k) => cand[k].found)) {
      notes.push(`Page ${set.page} has 1099-B box labels but no amounts next to them.`);
      continue;
    }
    const dup = bForms.find((f) => same1099B(f, cand));
    if (dup) dup.pages = [...new Set([...dup.pages, set.page])];
    else bForms.push(cand);
  }
  forms.push(...bForms);

  // 1099-NEC, 1099-MISC and 1099-K: one income box each.
  const payerForms: PayerFormCandidate[] = [];
  const miscSeen = formsSeen.includes("1099-MISC");
  for (const a of anchors) {
    let kind: PayerFormCandidate["kind"] | null = null;
    if (a.box === "nec1a") kind = "form_1099nec";
    else if (a.box === "misc3" && (miscSeen || a.prefix === 3)) kind = "form_1099misc";
    else if (a.box === "k1a") kind = "form_1099k";
    if (!kind) continue;
    const incomeBox = valueFor(a, lines, anchors);
    // A heading for another form between the income box and a box 4 means that box 4 belongs to the other form.
    const name = FORM_NAME[kind];
    const headingBetween = (x: Anchor) =>
      anchors.some((f) => (f.box === "ctype" || (f.box === "form" && f.text !== name)) && f.page === a.page && f.lineIndex > Math.min(a.lineIndex, x.lineIndex) && f.lineIndex < Math.max(a.lineIndex, x.lineIndex));
    const b4s = anchors.filter((x) => x.box === "b4" && x.page === a.page && Math.abs(x.line.y - a.line.y) <= 200 && !headingBetween(x));
    const box4: Found<Amount> = b4s.length === 1 ? valueFor(b4s[0]!, lines, anchors) : b4s.length ? { found: false, reason: "ambiguous", page: a.page } : NOT_FOUND;
    if (!incomeBox.found && !box4.found && !formsSeen.includes(FORM_NAME[kind])) continue;
    const cand: PayerFormCandidate = { kind, taxYear: docYear, issuer: payerOn(a.page, lines, anchors), corrected: correctedOn(a.page, anchors), incomeBox, box4, pages: [a.page] };
    const dup = payerForms.find((f) => f.kind === kind && sameAmount(f.incomeBox, incomeBox) && sameAmount(f.box4, box4));
    if (dup) dup.pages = [...new Set([...dup.pages, a.page])];
    else payerForms.push(cand);
  }
  forms.push(...payerForms);

  if (!hasText) notes.push("The PDF has no readable text. It's probably a scanned image.");
  else if (!forms.length) notes.push(formsSeen.length ? `Found ${formsSeen.join(", ")} but none of the boxes your tax report uses.` : "No 1099 box labels were found in the text.");
  if (!docYear.found && docYear.reason === "ambiguous") notes.push(`The PDF mentions more than one tax year: ${docYear.raw}.`);
  return { forms, formsSeen, hasText, notes };
}

/** Identity of one candidate inside one file, so each box set of a composite statement is added once. */
export function candidateFingerprint(fileHash: string, c: FormCandidate): string {
  const amt = (f: Found<Amount>) => (f.found && f.value.state === "value" ? f.value.value : "?");
  if (c.kind === "form_1099b") return `${fileHash}:1099b:${c.description.found ? c.description.value.toLowerCase() : "?"}:${amt(c.box8)}:${amt(c.box9)}:${amt(c.box10)}:${amt(c.box11)}`;
  return `${fileHash}:${c.kind}:${amt(c.incomeBox)}`;
}
